"use client"

import { useEffect, useState, type ReactNode } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import BannerImage from "@/components/banners/banner-image"
import BannerLink from "@/components/banners/banner-link"
import {
  BANNER_AUTOPLAY_DELAY,
  getBannerLayers,
  type BannerType,
  type SiteBanner,
} from "@/lib/banners"
import styles from "./stacked-banner-carousel.module.css"

export default function StackedBannerCarousel({
  banners,
  type = 0,
  userId,
}: {
  banners: SiteBanner[]
  type?: BannerType
  userId?: string
}) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [pageVisible, setPageVisible] = useState(true)
  const [reduceMotion, setReduceMotion] = useState(false)
  const count = banners.length
  const autoplay = count > 1 && !reduceMotion

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => setReduceMotion(preference.matches)
    updatePreference()
    preference.addEventListener("change", updatePreference)
    return () => preference.removeEventListener("change", updatePreference)
  }, [])

  useEffect(() => {
    const updateVisibility = () => setPageVisible(!document.hidden)
    updateVisibility()
    document.addEventListener("visibilitychange", updateVisibility)
    return () =>
      document.removeEventListener("visibilitychange", updateVisibility)
  }, [])

  useEffect(() => {
    if (!autoplay || hovered || focused || !pageVisible) return
    const interval = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % count)
    }, BANNER_AUTOPLAY_DELAY)
    return () => window.clearInterval(interval)
  }, [autoplay, hovered, focused, pageVisible, count])

  if (!count) return null

  const current = activeIndex % count
  const advance = (direction: number) => {
    setActiveIndex((index) => (index + direction + count) % count)
  }

  return (
    <section
      aria-label={type === 0 ? "Home banners" : "Brands banners"}
      aria-roledescription="carousel"
      data-active-banner={banners[current]?.id}
      className="group/banner mx-auto max-w-[1440px] px-1 pt-6 pb-8 sm:px-[72px] sm:pt-8 sm:pb-8"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocused(false)
      }}
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault()
          advance(event.key === "ArrowLeft" ? -1 : 1)
        }
      }}
    >
      <div className={styles.stack}>
        <div aria-hidden="true" className={styles.sizer} />
        {getBannerLayers(banners, current).map(({ banner, index, depth }) => {
          const inset = `calc(var(--banner-inset) * ${(2 - depth) / 2})`
          const verticalInset = depth === 0 ? "0%" : depth === 1 ? "6%" : "14%"
          return (
            <div
              key={banner.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${count}`}
              aria-hidden={depth !== 0}
              inert={depth !== 0}
              data-banner-depth={depth}
              className={styles.slide}
              style={{
                zIndex: 3 - depth,
                opacity: depth === 0 ? 1 : depth === 1 ? 0.7 : 0.4,
                pointerEvents: depth === 0 ? "auto" : "none",
                left: inset,
                right: inset,
                top: verticalInset,
                bottom: verticalInset,
              }}
            >
              <BannerLink
                banner={banner}
                active={depth === 0}
                index={index}
                userId={userId}
              >
                <BannerImage
                  banner={banner}
                  alt={`Featured offer ${index + 1}`}
                  sizes="(max-width: 639px) 303px, (max-width: 1439px) calc(100vw - 240px), 1200px"
                  mobileSizes="303px"
                  priority={index === 0}
                  loading={index === 0 ? undefined : "eager"}
                  className={
                    depth === 0
                      ? "object-cover sm:object-contain"
                      : "object-cover"
                  }
                />
              </BannerLink>
            </div>
          )
        })}
        {count > 1 && (
          <div className="pointer-events-none absolute inset-0 z-10 opacity-100 transition-opacity group-focus-within/banner:opacity-100 group-hover/banner:opacity-100 sm:opacity-0">
            <BannerControl
              label="Previous banner"
              onClick={() => advance(-1)}
              className="top-1/2 -left-2 -translate-y-1/2 sm:left-2"
            >
              <ChevronLeft size={20} />
            </BannerControl>
            <BannerControl
              label="Next banner"
              onClick={() => advance(1)}
              className="top-1/2 -right-2 -translate-y-1/2 sm:right-2"
            >
              <ChevronRight size={20} />
            </BannerControl>
          </div>
        )}
      </div>
    </section>
  )
}

function BannerControl({
  label,
  onClick,
  className,
  children,
}: {
  label: string
  onClick: () => void
  className: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={`pointer-events-auto absolute flex size-8 items-center justify-center rounded-full border border-[#E5E6EB] bg-white/95 text-[#1D2129] shadow-sm hover:bg-[#F2FFF8] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0F9E4F] ${className}`}
    >
      {children}
    </button>
  )
}
